import * as Sentry from "@sentry/react";

export interface WidgetHostSentryOptions {
  /** Semantic version (e.g. monorepo package.json). */
  release: string;
  /** Shown in Sentry `app` context — distinguish earn vs tokenization hosts. */
  appDisplayName: string;
}

interface SentryConfig {
  dsn: string;
  environment: string;
  sendDefaultPii: boolean;
  tracesSampleRate: number;
  beforeSend?: (
    event: Sentry.ErrorEvent,
    hint: Sentry.EventHint,
  ) => Sentry.ErrorEvent | PromiseLike<Sentry.ErrorEvent | null> | null;
}

const getSentryConfig = (release: string): SentryConfig => {
  const mode = import.meta.env.MODE || "development";
  const isProduction = mode === "production";

  const environment =
    isProduction && typeof window !== "undefined" ? window.location.hostname : mode;

  return {
    // The Sentry DSN is intentionally embedded in source: it is a client-side ingest key
    // designed to be public (it is visible in every browser's network panel). It cannot
    // write to or read from Sentry projects — only submit events. Rotation requires a
    // code change, which is an accepted trade-off for this project.
    dsn: "https://7c42d57a265ea35372bd16d8726342df@o1012318.ingest.us.sentry.io/4509445690687488",
    environment,
    sendDefaultPii: true,
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    beforeSend: (event) => {
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.value?.includes("development") || error?.value?.includes("dev-server")) {
          return null;
        }
      }

      if (event.contexts) {
        event.contexts.widget = {
          version: release,
          mode,
          environment,
        };
      }

      return event;
    },
  };
};

export const initializeSentry = (options: WidgetHostSentryOptions): void => {
  const mode = import.meta.env.MODE || "development";
  const isDebugMode = import.meta.env.VITE_DEBUG === "true";
  const isProduction = mode === "production";

  const isPlaywrightTest = import.meta.env.VITE_PLAYWRIGHT_TEST === "true";

  if (isPlaywrightTest) {
    return;
  }

  if (mode === "development" && !isDebugMode) {
    return;
  }

  const config = getSentryConfig(options.release);

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    sendDefaultPii: config.sendDefaultPii,
    tracesSampleRate: config.tracesSampleRate,
    beforeSend: config.beforeSend,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        autoInject: false,
      }),
    ],

    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    release: options.release,

    ignoreErrors: [],

    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  });

  Sentry.setContext("app", {
    name: options.appDisplayName,
    version: options.release,
  });
};
